/**
 * Revenues Page
 * 
 * Management of revenues for the current budget period.
 */
import { type ReactNode, useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  ArrowUpRight,
  ArrowUpDown,
  Copy,
  Pencil,
  Trash2,
  Loader2,
  X,
  Info,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { DateInput } from '@/components/ui/date-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  PeriodNavigator,
  TransactionCard,
  CurrencyDisplay,
  FinancialSummaryCards,
  BulkSelectionSummary,
} from '@/components/financial';
import {
  parseDateStringToLocalDate,
  getBudgetPeriodPermissions,
  isDateWithinPeriod,
  getDefaultRealizationDate,
  formatDatePtBR,
} from '@/lib/date';
import { normalizeSearchText } from '@/lib/text';
import { getInstallmentPlannedAmount } from '@/lib/installments';
import {
  useRevenues,
  useOverdueRevenues,
  useUpcomingRevenues,
  useRevenuesByPeriod,
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useCreateRevenue,
  useUpdateRevenue,
  useRealizeRevenue,
  useUndoRevenueRealization,
  useDeleteRevenue,
  useCopyRevenuesFromPrevious,
  useRevenueCopySourcePeriods,
  useAddFractionalRealization,
  useUpdateFractionalRealization,
  useDeleteFractionalRealization,
  useDeleteFutureExpense,
  useFinancialList,
  useAgents,
  useFinancialSettings,
} from '@/hooks/use-financial';
import { FeatureGuard } from '@/hooks/use-plan-features';
import { useGatedTypeSelect } from '@/hooks/use-gated-type-select';
import { REVENUE_TYPE_FEATURE, LOCKED_REVENUE_TYPE_MESSAGE } from '@/config/features';
import { FeatureLockButton } from '@/components/subscription/FeatureBlockedPage';
import {
  FIRST_ACCESS_TUTORIAL_DEMO,
  FIRST_ACCESS_TUTORIAL_STEPS,
  getTutorialDateInBudgetPeriod,
  markTutorialKnownItemIds,
  findTutorialCreatedItem,
  requestTutorialAdvance,
  useActiveOnboardingStep,
} from '@/components/onboarding/tutorial-runtime';

const TUTORIAL_SCOPE = 'revenues';

// Realce do botão de criação quando o formulário está completo.
const CREATE_READY_BUTTON_CLASS =
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600';
import type { FractionalRealization, Revenue, RevenueType, RevenueCreate } from '@/types/financial';
import { DueEmptyState, DueFilterTabsList, type DueTab } from '@/components/financial/DueFilterTabs';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { UPCOMING_DAYS } from '@/lib/dueWindow';

const revenueTypeLabels: Record<string, string> = {
  continuous: 'Contínua',
  single: 'Única',
  installment: 'Parcelada',
  fractional: 'Fracionada',
  CONTINUOUS: 'Contínua',
  SINGLE: 'Única',
  INSTALLMENT: 'Parcelada',
  FRACTIONAL: 'Fracionada',
};

const revenueTypeIcons: Record<string, string> = {
  continuous: '🔄',
  single: '1️⃣',
  installment: '📊',
  fractional: '📈',
  CONTINUOUS: '🔄',
  SINGLE: '1️⃣',
  INSTALLMENT: '📊',
  FRACTIONAL: '📈',
};

const getRevenuePlannedAmountForPO = (revenue: Revenue): number =>
  getInstallmentPlannedAmount({
    plannedAmount: revenue.planned_amount,
    isInstallment: revenue.revenue_type === 'installment',
    currentInstallment: revenue.current_installment,
    totalInstallments: revenue.total_installments,
  });

const formatPeriodLabel = (startDate?: string, endDate?: string) => {
  const start = startDate ? parseDateStringToLocalDate(startDate) : null;
  const end = endDate ? parseDateStringToLocalDate(endDate) : null;
  const startLabel = start ? start.toLocaleDateString('pt-BR') : startDate || '';
  const endLabel = end ? end.toLocaleDateString('pt-BR') : endDate || '';
  return `${startLabel} - ${endLabel}`.trim();
};

const getPONumberByStartDate = (startDate?: string, fallback?: number): number => {
  const start = startDate ? parseDateStringToLocalDate(startDate) : null;
  if (start) return start.getMonth() + 1;
  return fallback ?? 1;
};

export function RevenuesPage() {
  const location = useLocation();
  const appliedPrefillRef = useRef<string | null>(null);
  // Despesa futura de origem a ser removida SOMENTE após a receita pré-preenchida ser
  // criada com sucesso. Fica "armado" ao aplicar o prefill e é limpo ao fechar/cancelar
  // a modal — assim, cancelar a receita preserva a despesa futura.
  const pendingFutureExpenseDeleteRef = useRef<string | null>(null);
  const appliedTutorialStepRef = useRef<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<DueTab>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRevenue, setSelectedRevenue] = useState<Revenue | null>(null);
  const [revenueToEdit, setRevenueToEdit] = useState<Revenue | null>(null);
  
  const activeTutorialStep = useActiveOnboardingStep();

  // Gating por tipo: Contínua/Parcelada/Fracionada exigem feature do plano;
  // Única é sempre livre. Os tipos aparecem travados (cadeado) quando fora do plano.
  const revenueTypeGate = useGatedTypeSelect(REVENUE_TYPE_FEATURE, LOCKED_REVENUE_TYPE_MESSAGE);
  // Default seguro: se Contínua estiver bloqueada, abre o form em Única.
  const defaultRevenueType: RevenueType = revenueTypeGate.isLocked('continuous')
    ? 'single'
    : 'continuous';

  // Form state for create
  const [formData, setFormData] = useState({
    name: '',
    revenue_type: defaultRevenueType as RevenueType,
    planned_amount: '',
    receipt_method: '',
    expected_date: '',
    notes: '',
    agent_id: '',
    // Installment fields
    total_installments: '',
    remaining_installments: '',
    // Fractional fields
    frequency: '',
    average_value: '',
  });

  const getInstallmentValidationErrors = (
    total: string,
    remaining: string
  ): { totalError: string | null; remainingError: string | null } => {
    const totalNum = Number.parseInt(total, 10);
    const remainingNum = Number.parseInt(remaining, 10);
    let totalError: string | null = null;
    let remainingError: string | null = null;

    if (Number.isFinite(totalNum) && totalNum === 1) {
      totalError = 'Trocar o tipo de receita para Única';
    }

    if (Number.isFinite(totalNum) && Number.isFinite(remainingNum) && remainingNum > totalNum) {
      remainingError = 'O número de parcelas restantes não pode ser maior que o total de parcelas.';
    }

    return { totalError, remainingError };
  };

  const createFormInstallmentErrors =
    formData.revenue_type === 'installment'
      ? getInstallmentValidationErrors(formData.total_installments, formData.remaining_installments)
      : { totalError: null, remainingError: null };
  const createFormTotalInstallmentError = createFormInstallmentErrors.totalError;
  const createFormRemainingInstallmentError = createFormInstallmentErrors.remainingError;

  /**
   * Formulário de nova receita pronto para envio. Serve para dois fins: manter o
   * botão desabilitado enquanto falta dado obrigatório e, no tutorial, pintá-lo
   * de verde assim que o usuário completa o preenchimento.
   */
  const isCreateFormComplete = useMemo(() => {
    if (!formData.name || !formData.planned_amount) return false;
    if (createFormTotalInstallmentError || createFormRemainingInstallmentError) return false;

    const { revenue_type: revenueType } = formData;

    if (revenueType === 'continuous' || revenueType === 'single') {
      return Boolean(formData.expected_date && formData.receipt_method && formData.agent_id);
    }

    if (revenueType === 'installment') {
      return Boolean(
        formData.expected_date &&
          formData.receipt_method &&
          formData.agent_id &&
          formData.total_installments &&
          formData.remaining_installments
      );
    }

    // Fracionada é o último tipo de RevenueType: não pede data, porque o valor
    // total vem de frequência x valor médio.
    return Boolean(
      formData.receipt_method && formData.agent_id && formData.frequency && formData.average_value
    );
  }, [formData, createFormTotalInstallmentError, createFormRemainingInstallmentError]);
  
  // Fractional realization dialog
  const [isFractionalDialogOpen, setIsFractionalDialogOpen] = useState(false);
  const [fractionalFormData, setFractionalFormData] = useState({
    amount: '',
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
    receipt_method: '',
    agent_id: '',
  });

  // Fractional analytics + edit entries (Resumo Analítico)
  const [isFractionalAnalyticsDialogOpen, setIsFractionalAnalyticsDialogOpen] = useState(false);
  const [fractionalAnalyticsRevenueId, setFractionalAnalyticsRevenueId] = useState<string>('');
  const [isFractionalEntryEditDialogOpen, setIsFractionalEntryEditDialogOpen] = useState(false);
  const [fractionalEntryToEdit, setFractionalEntryToEdit] = useState<FractionalRealization | null>(null);
  const [fractionalEntryFormData, setFractionalEntryFormData] = useState({
    amount: '',
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
    receipt_method: '',
    agent_id: '',
  });

  // General realization dialog
  const [isRealizeDialogOpen, setIsRealizeDialogOpen] = useState(false);
  const [realizeFormData, setRealizeFormData] = useState({
    amount: '',
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    receipt_method: '',
    notes: '',
    agent_id: '',
  });

  // Edit realized dialog (for continuous, single, installment)
  const [isEditRealizedDialogOpen, setIsEditRealizedDialogOpen] = useState(false);
  const [isUndoRealizedDialogOpen, setIsUndoRealizedDialogOpen] = useState(false);
  const [editRealizedFormData, setEditRealizedFormData] = useState({
    amount: '',
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    receipt_method: '',
    agent_id: '',
  });
  
  // Form state for edit
  const [editFormData, setEditFormData] = useState({
    name: '',
    revenue_type: defaultRevenueType as RevenueType,
    planned_amount: '',
    realized_amount: '',
    receipt_method: '',
    expected_date: '',
    agent_id: '',
    notes: '',
    total_installments: '',
    remaining_installments: '',
    frequency: '',
    average_value: '',
  });
  const [isEditScopeDialogOpen, setIsEditScopeDialogOpen] = useState(false);
  const [editScope, setEditScope] = useState<'current' | 'future'>('current');
  const [isTypeChangeDialogOpen, setIsTypeChangeDialogOpen] = useState(false);
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [isDeleteScopeDialogOpen, setIsDeleteScopeDialogOpen] = useState(false);
  const [revenueToDelete, setRevenueToDelete] = useState<Revenue | null>(null);
  const [deleteScope, setDeleteScope] = useState<'current' | 'future'>('current');
  const [isCopyPreviousDialogOpen, setIsCopyPreviousDialogOpen] = useState(false);
  const [copyDataScope, setCopyDataScope] = useState<'planned' | 'realized'>('planned');
  const [copySourcePeriodId, setCopySourcePeriodId] = useState<string>('');
  const [selectedCopyRevenueIds, setSelectedCopyRevenueIds] = useState<string[]>([]);
  const [selectedRevenueIds, setSelectedRevenueIds] = useState<string[]>([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const { data: currentPeriod, isLoading: isPeriodLoading } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();
  const { data: revenues, isLoading: isRevenuesLoading } = useRevenues(currentPeriod?.id);
  const overdueQuery = useOverdueRevenues(currentPeriod?.id);
  const upcomingQuery = useUpcomingRevenues(UPCOMING_DAYS, currentPeriod?.id);
  const overdueRevenues = overdueQuery.data;
  const upcomingRevenues = upcomingQuery.data;
  const { data: receiptMethodsList } = useFinancialList('receipt_methods');
  const { data: revenueNamesList } = useFinancialList('revenue_names');
  const { data: agents } = useAgents();
  const revenueAgents = useMemo(() => {
    const allAgents = agents || [];
    const preferredAgents = allAgents.filter(
      (agent) => agent.is_default_receiver || agent.is_default_agent
    );
    return preferredAgents.length > 0 ? preferredAgents : allAgents;
  }, [agents]);
  const defaultRevenueAgentId =
    revenueAgents.find((agent) => agent.is_default_agent)?.id ||
    revenueAgents.find((agent) => agent.is_default_receiver)?.id ||
    revenueAgents[0]?.id ||
    '';
  const tutorialReceiptMethod =
    receiptMethodsList?.items?.[0]?.name || FIRST_ACCESS_TUTORIAL_DEMO.revenue.receiptMethod;
  const { data: financialSettings } = useFinancialSettings();
  const { mutate: copyFromPrevious, isPending: isCopying } = useCopyRevenuesFromPrevious();
  const copyTargetPeriodId = useMemo(() => {
    const navPeriod = navigation?.periods?.[navigation.current_index];
    return currentPeriod?.id ?? navPeriod?.id ?? '';
  }, [navigation, currentPeriod]);
  const copyPreviousPeriodId = navigation?.previous_period?.id;

  const { data: copySourcePeriods, isLoading: isCopySourcesLoading } = useRevenueCopySourcePeriods(
    copyTargetPeriodId,
    copyDataScope,
    isCopyPreviousDialogOpen
  );
  const { data: copySourceRevenues, isLoading: isCopySourceRevenuesLoading } = useRevenuesByPeriod(
    copySourcePeriodId,
    isCopyPreviousDialogOpen && !!copySourcePeriodId
  );
  const { mutate: createRevenue, isPending: isCreating } = useCreateRevenue();
  const { mutate: deleteFutureExpense } = useDeleteFutureExpense();
  const { mutate: updateRevenue, isPending: isUpdating } = useUpdateRevenue();
  const { mutate: deleteRevenue, mutateAsync: deleteRevenueAsync, isPending: isDeletingRevenue } = useDeleteRevenue();
  const { mutate: realizeRevenue } = useRealizeRevenue();
  const { mutate: undoRevenueRealization, isPending: isUndoingRealization } =
    useUndoRevenueRealization();
  const { mutate: addFractionalRealization, isPending: isAddingFractional } = useAddFractionalRealization();
  const { mutate: updateFractionalRealization, isPending: isUpdatingFractionalRealization } =
    useUpdateFractionalRealization();
  const { mutate: deleteFractionalRealization, isPending: isDeletingFractionalRealization } =
    useDeleteFractionalRealization();

  const periodPermissions = getBudgetPeriodPermissions({
    startDate: currentPeriod?.start_date,
    endDate: currentPeriod?.end_date,
    paymentDay: financialSettings?.default_payment_day ?? currentPeriod?.payment_day,
  });
  const canPlanInPeriod = periodPermissions.canPlan;
  const canRealizeInPeriod = periodPermissions.canRealize;

  const locationState = location.state as {
    revenuePrefill?: {
      name?: string;
      revenue_type?: RevenueType;
      planned_amount?: number;
      receipt_method?: string;
      expected_date?: string;
      notes?: string;
      agent_id?: string;
    };
    // Quando a receita nasce de uma despesa futura ("Adicionar como Receita"), este id
    // indica qual despesa deve ser removida — mas apenas após a receita ser criada.
    sourceFutureExpenseId?: string;
  } | null;
  const revenuePrefill = locationState?.revenuePrefill;
  const sourceFutureExpenseId = locationState?.sourceFutureExpenseId;

  const isLoading = isPeriodLoading || isRevenuesLoading;
  const fractionalAnalyticsRevenue =
    revenues?.find((r) => r.id === fractionalAnalyticsRevenueId) ?? null;
  const tutorialDateInPeriod = getTutorialDateInBudgetPeriod(currentPeriod);
  const tutorialRevenue = useMemo(
    () => findTutorialCreatedItem(TUTORIAL_SCOPE, revenues),
    [revenues]
  );

  const copyCandidates = useMemo(() => {
    const fractionalRevenues =
      (copySourceRevenues || []).filter(
        (revenue) => revenue.revenue_type === 'fractional' || revenue.revenue_type === 'FRACTIONAL'
      ) || [];

    if (copyDataScope === 'realized') {
      return fractionalRevenues.filter((revenue) => {
        const realizedAmount = Number(revenue.realized_amount || 0);
        const realizationCount = revenue.fractional_realizations?.length ?? 0;
        return realizationCount > 0 || realizedAmount > 0;
      });
    }

    return fractionalRevenues;
  }, [copySourceRevenues, copyDataScope]);

  const filteredCopySourcePeriods = useMemo(() => {
    if (!copyPreviousPeriodId) return [];
    return (copySourcePeriods || []).filter((period) => period.id === copyPreviousPeriodId);
  }, [copySourcePeriods, copyPreviousPeriodId]);

  const selectedCopyRevenueCount = selectedCopyRevenueIds.length;
  const allCopyRevenuesSelected =
    copyCandidates.length > 0 && selectedCopyRevenueCount === copyCandidates.length;
  const someCopyRevenuesSelected = selectedCopyRevenueCount > 0 && !allCopyRevenuesSelected;

  // Reset seleção ao fechar o modal de cópia
  useEffect(() => {
    if (isCopyPreviousDialogOpen) return;
    setCopySourcePeriodId('');
    setCopyDataScope('planned');
    setSelectedCopyRevenueIds([]);
  }, [isCopyPreviousDialogOpen]);

  // Quando trocar o escopo (planejamento/realização), reseta o período e seleção
  useEffect(() => {
    if (!isCopyPreviousDialogOpen) return;
    setCopySourcePeriodId('');
    setSelectedCopyRevenueIds([]);
  }, [copyDataScope, isCopyPreviousDialogOpen]);

  // Quando trocar o período, reseta seleção
  useEffect(() => {
    if (!isCopyPreviousDialogOpen) return;
    setSelectedCopyRevenueIds([]);
  }, [copySourcePeriodId, isCopyPreviousDialogOpen]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isCreateDialogOpen) {
      setFormData({
        name: '',
        revenue_type: defaultRevenueType,
        planned_amount: '',
        receipt_method: '',
        expected_date: '',
        notes: '',
        total_installments: '',
        remaining_installments: '',
        frequency: '',
        average_value: '',
        agent_id: '',
      });
      // Fechou/cancelou a modal sem criar a receita: desarma a exclusão pendente para
      // não remover a despesa futura de origem indevidamente.
      pendingFutureExpenseDeleteRef.current = null;
    }
  }, [isCreateDialogOpen, defaultRevenueType]);

  useEffect(() => {
    if (!revenuePrefill || !canPlanInPeriod) return;
    const token = JSON.stringify(revenuePrefill);
    if (appliedPrefillRef.current === token) return;
    appliedPrefillRef.current = token;
    // Arma a exclusão da despesa de origem; só será executada se a receita for criada.
    pendingFutureExpenseDeleteRef.current = sourceFutureExpenseId ?? null;

    setIsCreateDialogOpen(true);
    setFormData((prev) => ({
      ...prev,
      name: revenuePrefill.name ?? prev.name,
      revenue_type: revenuePrefill.revenue_type ?? prev.revenue_type,
      planned_amount:
        revenuePrefill.planned_amount !== undefined && revenuePrefill.planned_amount !== null
          ? String(revenuePrefill.planned_amount)
          : prev.planned_amount,
      receipt_method: revenuePrefill.receipt_method ?? prev.receipt_method,
      expected_date: revenuePrefill.expected_date ?? prev.expected_date,
      notes: revenuePrefill.notes ?? prev.notes,
      agent_id: revenuePrefill.agent_id ?? prev.agent_id,
      total_installments: '',
      remaining_installments: '',
      frequency: '',
      average_value: '',
    }));
  }, [revenuePrefill, sourceFutureExpenseId, canPlanInPeriod]);

  // Ao abrir o modal de criação, pré-preenche o agente de receita (priorizando Agente Padrão)
  useEffect(() => {
    if (!isCreateDialogOpen) return;

    if (!defaultRevenueAgentId) return;

    setFormData((prev) => (prev.agent_id ? prev : { ...prev, agent_id: defaultRevenueAgentId }));
  }, [defaultRevenueAgentId, isCreateDialogOpen]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.revenueSave;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!canPlanInPeriod || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    // Fotografa o que já existe: o registro que sobrar depois é o lançamento
    // real que o usuário acabou de criar no tutorial.
    markTutorialKnownItemIds(TUTORIAL_SCOPE, (revenues || []).map((revenue) => revenue.id));
    setIsCreateDialogOpen(true);
    setFormData({
      // O nome fica vazio de propósito: o usuário escolhe, na listagem
      // existente, uma receita verdadeira dele.
      name: '',
      revenue_type: 'continuous',
      planned_amount: String(FIRST_ACCESS_TUTORIAL_DEMO.revenue.amount),
      receipt_method: tutorialReceiptMethod,
      expected_date: tutorialDateInPeriod,
      notes: '',
      agent_id: defaultRevenueAgentId,
      total_installments: '',
      remaining_installments: '',
      frequency: '',
      average_value: '',
    });
  }, [
    activeTutorialStep,
    canPlanInPeriod,
    defaultRevenueAgentId,
    tutorialDateInPeriod,
    tutorialReceiptMethod,
    defaultRevenueType,
    revenues,
  ]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.revenueConfirm;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!tutorialRevenue || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    setSelectedRevenue(tutorialRevenue);
    setRealizeFormData({
      amount: String(getRevenuePlannedAmountForPO(tutorialRevenue) || FIRST_ACCESS_TUTORIAL_DEMO.revenue.amount),
      realization_date: tutorialRevenue.expected_date || tutorialDateInPeriod,
      receipt_method: tutorialRevenue.receipt_method || tutorialReceiptMethod,
      // Sem observação de tutorial: o recebimento é de uma receita real do usuário.
      notes: '',
      agent_id: tutorialRevenue.agent_id || defaultRevenueAgentId || '',
    });
    setIsRealizeDialogOpen(true);
  }, [
    activeTutorialStep,
    defaultRevenueAgentId,
    tutorialDateInPeriod,
    tutorialReceiptMethod,
    tutorialRevenue,
  ]);
  
  // Update planned_amount when frequency/average_value changes for fractional
  useEffect(() => {
    if (formData.revenue_type === 'fractional' && formData.frequency && formData.average_value) {
      const total = Number.parseInt(formData.frequency) * Number.parseFloat(formData.average_value);
      setFormData(prev => ({ ...prev, planned_amount: total.toString() }));
    }
  }, [formData.revenue_type, formData.frequency, formData.average_value]);

  // Update planned_amount when frequency/average_value changes for edit (fractional)
  useEffect(() => {
    if (editFormData.revenue_type === 'fractional' && editFormData.frequency && editFormData.average_value) {
      const total = Number.parseInt(editFormData.frequency) * Number.parseFloat(editFormData.average_value);
      setEditFormData((prev) => ({ ...prev, planned_amount: total.toString() }));
    }
  }, [editFormData.revenue_type, editFormData.frequency, editFormData.average_value]);

  const handleCreateRevenue = () => {
    if (!formData.name || !formData.planned_amount || !currentPeriod?.id) return;
    const isContinuousOrSingle = formData.revenue_type === 'continuous' || formData.revenue_type === 'single';
    if (isContinuousOrSingle && (!formData.expected_date || !formData.receipt_method || !formData.agent_id)) return;

    const isInstallment = formData.revenue_type === 'installment';
    if (isInstallment) {
      if (!formData.expected_date || !formData.receipt_method || !formData.agent_id) return;
      if (!formData.total_installments || !formData.remaining_installments) return;
      if (createFormTotalInstallmentError || createFormRemainingInstallmentError) return;
    }

    const isFractional = formData.revenue_type === 'fractional';
    if (isFractional) {
      if (!formData.receipt_method || !formData.agent_id) return;
      if (!formData.frequency || !formData.average_value) return;
    }
    
    const plannedAmount = (() => {
      if (!isInstallment) return Number.parseFloat(formData.planned_amount);

      const totalInstallments = Number.parseInt(formData.total_installments, 10);
      const installmentValue = Number.parseFloat(formData.planned_amount);
      const installmentCents = Math.round(installmentValue * 100);
      const totalCents = installmentCents * totalInstallments;
      return totalCents / 100;
    })();

    const data: RevenueCreate = {
      name: formData.name,
      revenue_type: formData.revenue_type,
      planned_amount: plannedAmount,
      receipt_method: formData.receipt_method || undefined,
      expected_date:
        formData.revenue_type === 'fractional' ? undefined : formData.expected_date || undefined,
      notes: formData.notes || undefined,
      agent_id: formData.agent_id || undefined,
      // Installment fields
      current_installment:
        isInstallment && formData.total_installments && formData.remaining_installments
          ? Number.parseInt(formData.total_installments, 10) - Number.parseInt(formData.remaining_installments, 10) + 1
          : undefined,
      total_installments: isInstallment && formData.total_installments ? Number.parseInt(formData.total_installments, 10) : undefined,
      // Fractional fields
      frequency: formData.revenue_type === 'fractional' && formData.frequency 
        ? Number.parseInt(formData.frequency) : undefined,
      average_value: formData.revenue_type === 'fractional' && formData.average_value 
        ? Number.parseFloat(formData.average_value) : undefined,
    };
    
    createRevenue({ data, periodId: currentPeriod?.id }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.revenueSave);
        // "Adicionar como Receita": só agora, com a receita já criada, removemos a
        // despesa futura de origem. Cancelar antes disso a mantém intacta.
        const futureExpenseIdToDelete = pendingFutureExpenseDeleteRef.current;
        if (futureExpenseIdToDelete) {
          deleteFutureExpense(futureExpenseIdToDelete);
          pendingFutureExpenseDeleteRef.current = null;
        }
        setIsCreateDialogOpen(false);
        // Reset form
        setFormData({
          name: '',
          revenue_type: defaultRevenueType,
          planned_amount: '',
          receipt_method: '',
          expected_date: '',
          notes: '',
          agent_id: '',
          total_installments: '',
          remaining_installments: '',
          frequency: '',
          average_value: '',
        });
      }
    });
  };
  
  // Valida que a data de recebimento pertence ao período do lançamento e, caso
  // contrário, notifica o usuário. Espelha a regra do backend (fonte de verdade).
  const validateRealizationDate = (dateStr?: string): boolean => {
    if (isDateWithinPeriod(dateStr, currentPeriod?.start_date, currentPeriod?.end_date)) {
      return true;
    }
    toast({
      title: 'Data fora do período orçamentário',
      description: `A data de recebimento deve estar dentro do período do lançamento (${formatDatePtBR(
        currentPeriod?.start_date
      )} a ${formatDatePtBR(currentPeriod?.end_date)}).`,
      variant: 'destructive',
    });
    return false;
  };

  const handleAddFractionalRealization = () => {
    if (!selectedRevenue || !fractionalFormData.amount) return;
    if (!validateRealizationDate(fractionalFormData.realization_date)) return;

    addFractionalRealization({
      id: selectedRevenue.id,
      data: {
        amount: Number.parseFloat(fractionalFormData.amount),
        realization_date: fractionalFormData.realization_date || undefined,
        receipt_method: fractionalFormData.receipt_method || undefined,
        agent_id: fractionalFormData.agent_id || undefined,
        notes: fractionalFormData.notes || undefined,
      }
    }, {
      onSuccess: () => {
        setIsFractionalDialogOpen(false);
        setSelectedRevenue(null);
        setFractionalFormData({
          amount: '',
          realization_date: format(new Date(), 'yyyy-MM-dd'),
          notes: '',
          receipt_method: '',
          agent_id: '',
        });
      }
    });
  };
  
  const handleOpenRealizeDialog = (revenue: Revenue) => {
    setSelectedRevenue(revenue);

    if (revenue.revenue_type === 'fractional') {
      setFractionalFormData({
        amount: revenue.average_value?.toString() || '',
        realization_date: getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
        notes: '',
        // Sugestão: a última forma usada nesta receita.
        receipt_method: revenue.last_receipt_method || '',
        // Quem recebeu: sugere o agente planejado, mas grava em campo próprio.
        agent_id: revenue.received_by_agent_id || revenue.agent_id || defaultRevenueAgentId || '',
      });
      setIsFractionalDialogOpen(true);
    } else {
      const plannedForPO = getRevenuePlannedAmountForPO(revenue);
      setRealizeFormData({
        amount: plannedForPO?.toString() || '',
        // Data padrão = hoje quando hoje pertence ao período; senão, clamp para o
        // limite do PO (evita pré-preencher com data fora de um PO já encerrado).
        realization_date: getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
        // Recebimento em aberto sugere a última forma usada; um já recebido
        // mostra a forma daquele recebimento.
        receipt_method: revenue.realized_receipt_method || revenue.last_receipt_method || '',
        notes: '',
        agent_id: revenue.received_by_agent_id || revenue.agent_id || defaultRevenueAgentId || '',
      });
      setIsRealizeDialogOpen(true);
    }
  };

  const handleRealizeRevenue = () => {
    if (!selectedRevenue || !realizeFormData.amount) return;
    if (!validateRealizationDate(realizeFormData.realization_date)) return;

    realizeRevenue({
      id: selectedRevenue.id,
      data: {
        realized_amount: Number.parseFloat(realizeFormData.amount),
        realized_date: realizeFormData.realization_date || undefined,
        receipt_method: realizeFormData.receipt_method || undefined,
        notes: realizeFormData.notes || undefined,
        agent_id: realizeFormData.agent_id || undefined,
      }
    }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.revenueConfirm);
        setIsRealizeDialogOpen(false);
        setSelectedRevenue(null);
      }
    });
  };

  const handleOpenEditRealizedDialog = (revenue: Revenue) => {
    setSelectedRevenue(revenue);
    setEditRealizedFormData({
      amount: String(revenue.realized_amount || ''),
      realization_date:
        revenue.realized_date ||
        getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
      receipt_method: revenue.realized_receipt_method || '',
      agent_id: revenue.received_by_agent_id || revenue.agent_id || defaultRevenueAgentId || '',
    });
    setIsEditRealizedDialogOpen(true);
  };

  const handleUpdateRealized = () => {
    if (!selectedRevenue || !editRealizedFormData.amount) return;
    if (!validateRealizationDate(editRealizedFormData.realization_date)) return;

    updateRevenue({
      id: selectedRevenue.id,
      data: {
        realized_amount: Number.parseFloat(editRealizedFormData.amount),
        realized_date: editRealizedFormData.realization_date || undefined,
        // Campos da realização: enviá-los como receipt_method/agent_id
        // reescreveria a Fonte Pagadora e o Agente da Receita planejados.
        realized_receipt_method: editRealizedFormData.receipt_method || undefined,
        received_by_agent_id: editRealizedFormData.agent_id || undefined,
      }
    }, {
      onSuccess: () => {
        setIsEditRealizedDialogOpen(false);
        setSelectedRevenue(null);
      }
    });
  };

  const handleConfirmUndoRealized = () => {
    if (!selectedRevenue) return;

    undoRevenueRealization(selectedRevenue.id, {
      onSuccess: () => {
        setIsUndoRealizedDialogOpen(false);
        setIsEditRealizedDialogOpen(false);
        setSelectedRevenue(null);
      },
    });
  };

  const handleOpenEditDialog = (revenue: Revenue) => {
    setRevenueToEdit(revenue);
    const plannedAmountForEdit =
      revenue.revenue_type === 'installment' ? getRevenuePlannedAmountForPO(revenue) : revenue.planned_amount;
    const totalInstallments = revenue.total_installments ?? 0;
    const currentInstallment = revenue.current_installment ?? 0;
    const remainingInstallments =
      totalInstallments && currentInstallment
        ? Math.max(totalInstallments - currentInstallment + 1, 1)
        : 0;
    setEditFormData({
      name: revenue.name,
      revenue_type: revenue.revenue_type,
      planned_amount:
        plannedAmountForEdit !== undefined && plannedAmountForEdit !== null
          ? String(plannedAmountForEdit)
          : '',
      realized_amount: String(revenue.realized_amount || ''),
      receipt_method: revenue.receipt_method || '',
      expected_date: revenue.expected_date || '',
      agent_id: revenue.agent_id || defaultRevenueAgentId || '',
      notes: revenue.notes || '',
      total_installments: revenue.total_installments ? String(revenue.total_installments) : '',
      remaining_installments: remainingInstallments ? String(remainingInstallments) : '',
      frequency: revenue.frequency ? String(revenue.frequency) : '',
      average_value: revenue.average_value ? String(revenue.average_value) : '',
    });
    setEditScope('current'); // Reset ao abrir o modal
    setIsEditScopeDialogOpen(false);
    setIsEditDialogOpen(true);
  };

  const handleOpenFractionalAnalyticsDialog = (revenue: Revenue) => {
    setFractionalAnalyticsRevenueId(revenue.id);
    setIsFractionalAnalyticsDialogOpen(true);
  };

  const handleOpenDeleteDialog = (revenue: Revenue) => {
    setRevenueToDelete(revenue);
    setDeleteScope('current'); // reset padrão

    if (revenue.revenue_type === 'continuous' || revenue.revenue_type === 'installment') {
      setIsDeleteConfirmDialogOpen(false);
      setIsDeleteScopeDialogOpen(true);
      return;
    }

    setIsDeleteScopeDialogOpen(false);
    setIsDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDelete = (applyToFuture: boolean) => {
    if (!revenueToDelete) return;

    deleteRevenue(
      { id: revenueToDelete.id, apply_to_future: applyToFuture },
      {
        onSuccess: () => {
          setIsDeleteConfirmDialogOpen(false);
          setIsDeleteScopeDialogOpen(false);
          setRevenueToDelete(null);
        },
      }
    );
  };

  const getFractionalCopyStats = (revenue: Revenue) => {
    const realizations = revenue.fractional_realizations ?? [];
    const realizedTotal = realizations.length
      ? realizations.reduce((sum, realization) => sum + Number(realization.amount || 0), 0)
      : Number(revenue.realized_amount || 0);
    const realizedCount = realizations.length;
    const averageRealized = realizedCount > 0 ? realizedTotal / realizedCount : 0;

    return {
      realizedTotal,
      realizedCount,
      averageRealized,
    };
  };

  const handleToggleCopyRevenueSelection = (revenueId: string, checked: boolean) => {
    setSelectedCopyRevenueIds((prev) => {
      if (checked) {
        return prev.includes(revenueId) ? prev : [...prev, revenueId];
      }
      return prev.filter((id) => id !== revenueId);
    });
  };

  const handleToggleCopyRevenueAll = (checked: boolean) => {
    if (checked) {
      setSelectedCopyRevenueIds(copyCandidates.map((revenue) => revenue.id));
      return;
    }
    setSelectedCopyRevenueIds([]);
  };

  const handleToggleRevenueSelection = (revenueId: string, checked: boolean) => {
    setSelectedRevenueIds((prev) => {
      if (checked) {
        return prev.includes(revenueId) ? prev : [...prev, revenueId];
      }
      return prev.filter((id) => id !== revenueId);
    });
  };

  const handleToggleAllRevenues = (checked: boolean) => {
    if (checked) {
      setSelectedRevenueIds(visibleRevenueIds);
      return;
    }
    setSelectedRevenueIds([]);
  };

  const handleBulkDeleteRevenues = async () => {
    if (selectedVisibleRevenueIds.length === 0) return;

    const idsToDelete = [...selectedVisibleRevenueIds];
    setIsBulkDeleting(true);

    const results = await Promise.allSettled(
      idsToDelete.map((id) => deleteRevenueAsync({ id, apply_to_future: true }))
    );

    const failedIds = idsToDelete.filter((_, index) => results[index]?.status === 'rejected');

    setIsBulkDeleting(false);

    if (failedIds.length === 0) {
      setSelectedRevenueIds([]);
      setIsBulkDeleteDialogOpen(false);
      return;
    }

    setSelectedRevenueIds(failedIds);
    toast({
      title: 'Algumas receitas não puderam ser excluídas',
      description: `${failedIds.length} de ${idsToDelete.length} exclusões falharam. Tente novamente.`,
      variant: 'destructive',
    });
  };

  const handleCopySelectedRevenues = () => {
    if (!copyTargetPeriodId || !copySourcePeriodId || selectedCopyRevenueIds.length === 0) return;

    copyFromPrevious(
      {
        target_period_id: copyTargetPeriodId,
        source_period_id: copySourcePeriodId,
        revenue_types: ['fractional'],
        revenue_ids: selectedCopyRevenueIds,
        copy_data: copyDataScope,
      },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          setIsCopyPreviousDialogOpen(false);
          setCopySourcePeriodId('');
          setSelectedCopyRevenueIds([]);
        },
      }
    );
  };

  const handleOpenFractionalEntryEditDialog = (entry: FractionalRealization) => {
    if (!fractionalAnalyticsRevenueId) return;
    setFractionalEntryToEdit(entry);
    setFractionalEntryFormData({
      amount: entry.amount?.toString() || '',
      realization_date:
        entry.realization_date ||
        getDefaultRealizationDate(currentPeriod?.start_date, currentPeriod?.end_date),
      notes: entry.notes || '',
      // A forma da parcela vive na própria realização.
      receipt_method: entry.receipt_method || '',
      agent_id:
        fractionalAnalyticsRevenue?.received_by_agent_id ||
        fractionalAnalyticsRevenue?.agent_id ||
        defaultRevenueAgentId ||
        '',
    });
    setIsFractionalAnalyticsDialogOpen(false);
    setIsFractionalEntryEditDialogOpen(true);
  };

  const handleCancelFractionalEntryEdit = () => {
    setIsFractionalEntryEditDialogOpen(false);
    setFractionalEntryToEdit(null);
    setIsFractionalAnalyticsDialogOpen(true);
  };

  const handleSaveFractionalEntryEdit = () => {
    if (!fractionalAnalyticsRevenueId || !fractionalEntryToEdit) return;
    if (!fractionalEntryFormData.amount || !fractionalEntryFormData.realization_date) return;
    if (!fractionalEntryFormData.agent_id || !fractionalEntryFormData.receipt_method) return;
    if (!validateRealizationDate(fractionalEntryFormData.realization_date)) return;

    updateFractionalRealization(
      {
        revenueId: fractionalAnalyticsRevenueId,
        realizationId: fractionalEntryToEdit.id,
        data: {
          amount: Number.parseFloat(fractionalEntryFormData.amount),
          realization_date: fractionalEntryFormData.realization_date,
          receipt_method: fractionalEntryFormData.receipt_method,
          agent_id: fractionalEntryFormData.agent_id,
          notes: fractionalEntryFormData.notes || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsFractionalEntryEditDialogOpen(false);
          setFractionalEntryToEdit(null);
          setIsFractionalAnalyticsDialogOpen(true);
        },
      }
    );
  };

  const handleDeleteFractionalEntry = (entry: FractionalRealization) => {
    if (!fractionalAnalyticsRevenueId) return;
    deleteFractionalRealization({ revenueId: fractionalAnalyticsRevenueId, realizationId: entry.id });
  };

  const submitUpdateRevenue = (applyToFuture: boolean) => {
    if (!revenueToEdit || !editFormData.name || !editFormData.planned_amount) return;

    const isInstallment = editFormData.revenue_type === 'installment';
    const isFractional = editFormData.revenue_type === 'fractional';
    const totalInstallments = isInstallment ? Number.parseInt(editFormData.total_installments, 10) : 0;
    const installmentValue = isInstallment ? Number.parseFloat(editFormData.planned_amount) : 0;

    const plannedAmount = (() => {
      if (!isInstallment) return Number.parseFloat(editFormData.planned_amount);

      if (
        revenueToEdit?.revenue_type === 'installment' &&
        Number.isFinite(installmentValue) &&
        Math.abs(installmentValue - getRevenuePlannedAmountForPO(revenueToEdit)) < 0.005
      ) {
        return Number(revenueToEdit.planned_amount);
      }

      const installmentCents = Math.round(installmentValue * 100);
      const totalCents = installmentCents * totalInstallments;
      return totalCents / 100;
    })();

    updateRevenue({
      id: revenueToEdit.id,
      data: {
        name: editFormData.name,
        revenue_type: editFormData.revenue_type,
        planned_amount: plannedAmount,
        realized_amount: editFormData.realized_amount ? Number.parseFloat(editFormData.realized_amount) : undefined,
        receipt_method: editFormData.receipt_method || undefined,
        expected_date: isFractional ? undefined : editFormData.expected_date || undefined,
        agent_id: editFormData.agent_id || undefined,
        notes: editFormData.notes || undefined,
        current_installment:
          isInstallment && editFormData.total_installments && editFormData.remaining_installments
            ? Number.parseInt(editFormData.total_installments, 10) -
              Number.parseInt(editFormData.remaining_installments, 10) +
              1
            : undefined,
        total_installments:
          isInstallment && editFormData.total_installments
            ? Number.parseInt(editFormData.total_installments, 10)
            : undefined,
        frequency:
          isFractional && editFormData.frequency ? Number.parseInt(editFormData.frequency, 10) : undefined,
        average_value:
          isFractional && editFormData.average_value ? Number.parseFloat(editFormData.average_value) : undefined,
        apply_to_future: applyToFuture,
      }
    }, {
      onSuccess: () => {
        setIsEditScopeDialogOpen(false);
        setIsTypeChangeDialogOpen(false);
        setIsEditDialogOpen(false);
        setRevenueToEdit(null);
      }
    });
  };

  const handleAttemptUpdateRevenue = () => {
    if (!revenueToEdit || !editFormData.name || !editFormData.planned_amount) {
      toast({
        variant: 'destructive',
        title: 'Preencha ao menos o nome e o valor planejado.',
      });
      return;
    }

    const isInstallment = editFormData.revenue_type === 'installment';
    if (isInstallment) {
      if (!editFormData.total_installments || !editFormData.remaining_installments) {
        toast({
          variant: 'destructive',
          title: 'Preencha o total e as parcelas restantes.',
        });
        return;
      }

      const totalInstallments = Number.parseInt(editFormData.total_installments, 10);
      const remainingInstallments = Number.parseInt(editFormData.remaining_installments, 10);

      if (Number.isFinite(totalInstallments) && totalInstallments === 1) {
        toast({
          variant: 'destructive',
          title: 'Troque o tipo da receita para “Única”.',
        });
        return;
      }

      if (
        Number.isFinite(totalInstallments) &&
        Number.isFinite(remainingInstallments) &&
        remainingInstallments > totalInstallments
      ) {
        toast({
          variant: 'destructive',
          title: 'Este número não pode ser maior que o total de parcelas',
        });
        return;
      }
    }

    const isFractional = editFormData.revenue_type === 'fractional';
    if (isFractional) {
      if (!editFormData.frequency || !editFormData.average_value) {
        toast({
          variant: 'destructive',
          title: 'Preencha a frequência e o valor médio planejado.',
        });
        return;
      }
    }

    const typeChanged = revenueToEdit.revenue_type !== editFormData.revenue_type;

    if (typeChanged) {
      setIsTypeChangeDialogOpen(true);
      return;
    }

    const needsScopeConfirm =
      revenueToEdit.revenue_type === 'continuous' ||
      revenueToEdit.revenue_type === 'installment' ||
      editFormData.revenue_type === 'continuous' ||
      editFormData.revenue_type === 'installment';

    if (needsScopeConfirm) {
      setEditScope('current');
      setIsEditScopeDialogOpen(true);
      return;
    }

    submitUpdateRevenue(false);
  };

  const handleConfirmEditScope = () => {
    submitUpdateRevenue(editScope === 'future');
  };

  const handleConfirmTypeChange = () => {
    submitUpdateRevenue(true);
  };

  // Lista da aba ativa (Todas / Vencidas / Próximos 7 dias); busca e filtros valem por cima.
  const tabRevenues = {
    all: revenues,
    overdue: overdueRevenues,
    upcoming: upcomingRevenues,
  }[activeTab];

  // Filter revenues (normalize case for comparison since backend may return uppercase values)
  const filteredRevenues = tabRevenues?.filter((revenue) => {
    const matchesSearch = normalizeSearchText(revenue.name).includes(normalizeSearchText(searchTerm));
    const matchesStatus = statusFilter === 'all' || revenue.status?.toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === 'all' || revenue.revenue_type?.toLowerCase() === typeFilter.toLowerCase();
    return matchesSearch && matchesStatus && matchesType;
  }) || [];

  // Sort revenues
  const sortedRevenues = [...filteredRevenues].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
        break;
      case 'date':
        // Use expected_date or created_at
        comparison = (a.expected_date || a.created_at) > (b.expected_date || b.created_at) ? 1 : -1;
        break;
      case 'value':
        comparison = Number(a.planned_amount) - Number(b.planned_amount);
        break;
      case 'type':
        comparison = (a.revenue_type || '').localeCompare(b.revenue_type || '');
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Aviso da aba Vencidas / Próximos 7 dias: carregando, erro ou vazia. A aba só
  // diz "tudo em dia" quando a lista CARREGOU e veio vazia — falha ou carregamento
  // não podem virar um falso "nenhuma receita vencida".
  const backToAllTab = () => setActiveTab('all');
  const dueTabQuery = { all: null, overdue: overdueQuery, upcoming: upcomingQuery }[activeTab];
  let emptyTabNotice: ReactNode = null;
  if (dueTabQuery?.isLoading) {
    emptyTabNotice = (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando receitas...
        </CardContent>
      </Card>
    );
  } else if (dueTabQuery?.isError) {
    emptyTabNotice = (
      <Card>
        <CardContent className="py-12 text-center text-sm text-destructive">
          Não foi possível carregar as receitas desta aba.
        </CardContent>
      </Card>
    );
  } else if (activeTab === 'overdue' && overdueRevenues?.length === 0) {
    emptyTabNotice = (
      <DueEmptyState
        title="Nenhuma receita vencida!"
        message="Todas as receitas estão em dia 🎉"
        backLabel="Voltar para todas as receitas"
        onBack={backToAllTab}
      />
    );
  } else if (activeTab === 'upcoming' && upcomingRevenues?.length === 0) {
    emptyTabNotice = (
      <DueEmptyState
        title={`Nenhuma receita nos próximos ${UPCOMING_DAYS} dias!`}
        message="Nada a receber nos próximos dias."
        backLabel="Voltar para todas as receitas"
        onBack={backToAllTab}
      />
    );
  }
  const visibleRevenueIds = sortedRevenues.map((revenue) => revenue.id);
  const selectedVisibleRevenueIds = selectedRevenueIds.filter((id) => visibleRevenueIds.includes(id));
  const selectedRevenueCount = selectedVisibleRevenueIds.length;
  const allRevenuesSelected =
    visibleRevenueIds.length > 0 && selectedRevenueCount === visibleRevenueIds.length;
  const someRevenuesSelected = selectedRevenueCount > 0 && !allRevenuesSelected;
  const selectedRevenues = sortedRevenues.filter((revenue) =>
    selectedVisibleRevenueIds.includes(revenue.id)
  );
  const selectedPlannedTotal = selectedRevenues.reduce(
    // Parceladas: soma o valor da parcela do período, não o total parcelado.
    (sum, revenue) => sum + getRevenuePlannedAmountForPO(revenue),
    0
  );
  const selectedRealizedTotal = selectedRevenues.reduce(
    (sum, revenue) => sum + Number(revenue.realized_amount || 0),
    0
  );

  // Calculate stats (convert from string/Decimal to number)

  useEffect(() => {
    setSelectedRevenueIds((prev) => {
      const next = prev.filter((id) => visibleRevenueIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleRevenueIds]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando receitas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Receitas</h1>
          <p className="text-muted-foreground">
            Gerencie suas fontes de renda do período
          </p>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <PeriodNavigator
            currentPeriod={currentPeriod}
            periods={navigation?.periods || []}
            isLoading={isNavigating}
            onNavigate={navigatePeriod}
            variant="boxed"
          />
        </div>
      </div>

      {/* Financial Summary Cards - sempre o 1º bloco de KPIs e fixo ao rolar */}
      <FinancialSummaryCards periodId={currentPeriod?.id} />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as DueTab)}
        className="space-y-4 sm:space-y-6"
      >
      <DueFilterTabsList
        allCount={revenues?.length}
        overdueCount={overdueRevenues?.length}
        upcomingCount={upcomingRevenues?.length}
      />

      {/* Filters */}
      <Card id="sheet">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            {/* Primary actions row */}
            <div className="flex gap-2 sm:gap-4">
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="gap-2 flex-1 sm:flex-none"
                disabled={!canPlanInPeriod}
                data-tour-id="create-revenue"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nova Receita</span>
                <span className="sm:hidden">Nova</span>
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar receitas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpar busca de receitas"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {/* Filters row */}
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="realized">Realizado</SelectItem>
                  <SelectItem value="partially_realized">Parcial</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="continuous">Contínua</SelectItem>
                  <SelectItem value="installment">Parcelada</SelectItem>
                  <SelectItem value="single">Única</SelectItem>
                  <SelectItem value="fractional">Fracionada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nome (A-Z)</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                  <SelectItem value="value">Valor</SelectItem>
                  <SelectItem value="type">Tipo</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="h-10 w-10 shrink-0"
                title={sortDirection === 'asc' ? 'Ordenação crescente' : 'Ordenação decrescente'}
              >
                <ArrowUpDown className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canPlanInPeriod && sortedRevenues.length > 0 && (
        <Card>
          <CardContent className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all-revenues"
                checked={allRevenuesSelected ? true : someRevenuesSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => handleToggleAllRevenues(checked === true)}
                aria-label="Selecionar todas as receitas"
              />
              <Label htmlFor="select-all-revenues" className="cursor-pointer">
                Selecionar Todos
              </Label>
            </div>
            {selectedRevenueCount > 0 && (
              <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <BulkSelectionSummary
                  plannedLabel="Planejado"
                  plannedTotal={selectedPlannedTotal}
                  realizedLabel="Realizado"
                  realizedTotal={selectedRealizedTotal}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                  onClick={() => setIsBulkDeleteDialogOpen(true)}
                  disabled={isBulkDeleting || isDeletingRevenue}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir ({selectedRevenueCount})
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenues List */}
      <TabsContent value={activeTab} className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto mt-0">
        {emptyTabNotice ?? (sortedRevenues.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ArrowUpRight className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-1">Nenhuma receita encontrada</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece adicionando suas fontes de renda'}
              </p>
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={!canPlanInPeriod}
              >
                <Plus className="h-4 w-4 mr-2" />
                Receber
              </Button>
            </CardContent>
          </Card>
        ) : (
          sortedRevenues.map((revenue) => {
            const isSelected = selectedRevenueIds.includes(revenue.id);

            return (
              <div key={revenue.id} className="relative">
                {canPlanInPeriod && (
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => handleToggleRevenueSelection(revenue.id, checked === true)}
                    aria-label={`Selecionar ${revenue.name}`}
                    className="absolute left-4 top-4 z-10"
                  />
                )}
                <TransactionCard
                    id={revenue.id}
                    name={revenue.name}
                    type="revenue"
                    subtype={`${revenueTypeIcons[revenue.revenue_type]} ${revenueTypeLabels[revenue.revenue_type]}${
                      revenue.revenue_type === 'installment' && revenue.current_installment && revenue.total_installments
                        ? ` (${revenue.current_installment}/${revenue.total_installments})`
                        : ''
                    }${
                      revenue.revenue_type === 'fractional' && revenue.frequency
                        ? ` (${revenue.fractional_realizations?.length || 0}/${revenue.frequency}x)`
                        : ''
                    }`}
                    plannedAmount={getRevenuePlannedAmountForPO(revenue)}
                    realizedAmount={revenue.realized_amount}
                    status={revenue.status}
                    hideVariance={revenue.revenue_type === 'installment'}
                    isBalanceCarryover={revenue.is_balance_carryover}
                    category={revenue.receipt_method}
                    dueDate={revenue.expected_date}
                    realizedDate={
                      revenue.revenue_type !== 'fractional'
                        ? revenue.realized_date
                        : (() => {
                            const realizations = revenue.fractional_realizations;
                            if (!realizations || realizations.length === 0) return revenue.realized_date;
                            const last = realizations.reduce<(typeof realizations)[number] | null>((acc, cur) => {
                              if (!acc) return cur;
                              return new Date(cur.created_at).getTime() > new Date(acc.created_at).getTime() ? cur : acc;
                            }, null);
                            return last?.realization_date;
                          })()
                    }
                    allowRealizeWhenRealized={
                      revenue.revenue_type === 'fractional' &&
                      (revenue.fractional_realizations?.length ?? 0) < (revenue.frequency ?? 0)
                    }
                    className={canPlanInPeriod ? 'pl-10 sm:pl-12' : undefined}
                    icon={ArrowUpRight}
                    showRealizeInDropdown={revenue.revenue_type !== 'fractional'}
                    onRealize={
                      revenue.revenue_type === 'fractional'
                        ? (revenue.fractional_realizations?.length ?? 0) < (revenue.frequency ?? 0)
                          ? () => handleOpenRealizeDialog(revenue)
                          : undefined
                        : revenue.status !== 'realized'
                          ? () => handleOpenRealizeDialog(revenue)
                          : undefined
                    }
                    realizeTourId={
                      tutorialRevenue?.id === revenue.id
                        ? FIRST_ACCESS_TUTORIAL_STEPS.revenueReceive
                        : undefined
                    }
                    disableRealize={!canRealizeInPeriod}
                    onEdit={
                      canPlanInPeriod
                        ? () => handleOpenEditDialog(revenue)
                        : undefined
                    }
                    onEditRealized={
                      revenue.revenue_type === 'fractional'
                        ? canPlanInPeriod
                          ? () => handleOpenFractionalAnalyticsDialog(revenue)
                          : undefined
                        : revenue.status === 'realized'
                          ? () => handleOpenEditRealizedDialog(revenue)
                          : undefined
                    }
                    onDelete={canPlanInPeriod ? () => handleOpenDeleteDialog(revenue) : undefined}
                  />
              </div>
            );
          })
        ))}
      </TabsContent>

      {/* Results summary */}
      {sortedRevenues.length > 0 && (
        <div className="text-sm text-muted-foreground text-center">
          Exibindo {sortedRevenues.length} de {tabRevenues?.length || 0} receitas
        </div>
      )}
      </Tabs>

      {/* Editar Realizado - Receita Fracionada */}
      <Dialog
        open={isFractionalAnalyticsDialogOpen}
        onOpenChange={(open) => {
          setIsFractionalAnalyticsDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle>
              Editar Realizado{fractionalAnalyticsRevenue?.name ? ` - ${fractionalAnalyticsRevenue.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Visualize e edite os recebimentos fracionados (editar/excluir) e compare Planejado x Realizado.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const realizations = fractionalAnalyticsRevenue?.fractional_realizations ?? [];
            const plannedCount = fractionalAnalyticsRevenue?.frequency ?? 0;
            const plannedAvg = Number(fractionalAnalyticsRevenue?.average_value || 0);
            const plannedTotal = Number(fractionalAnalyticsRevenue?.planned_amount || 0);
            const realizedTotal = realizations.reduce((acc, r) => acc + Number(r.amount || 0), 0);
            const realizedCount = realizations.length;
            const realizedAvg = realizedCount > 0 ? realizedTotal / realizedCount : 0;

            const formatDateDDMMYYYY = (value?: string) => {
              if (!value) return '';
              // Evita D-1: para date-only, formatamos por string, sem Date()
              if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [y, m, d] = value.split('-');
                return `${d}/${m}/${y}`;
              }
              return value;
            };

            const sorted = [...realizations].sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );

            return (
              <div className="space-y-6 py-2">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-emerald-600">Detalhamento dos Recebimentos</h3>
                  <div className="rounded-lg border bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Forma de Pagamento</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead className="text-right">Ação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sorted.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                              Nenhum recebimento lançado ainda.
                            </TableCell>
                          </TableRow>
                        ) : (
                          sorted.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell className="font-medium">{formatDateDDMMYYYY(r.realization_date)}</TableCell>
                              <TableCell>{r.receipt_method || '-'}</TableCell>
                              <TableCell className="text-right">
                                <CurrencyDisplay value={r.amount} />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleOpenFractionalEntryEditDialog(r)}
                                    aria-label="Editar recebimento"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    disabled={isDeletingFractionalRealization}
                                    onClick={() => handleDeleteFractionalEntry(r)}
                                    aria-label="Excluir recebimento"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-emerald-600">Comparação Planejado x Realizado</h3>
                  <div className="rounded-lg border bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Valor Médio</TableHead>
                          <TableHead className="text-center">Frequência</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">
                            <span className="mr-2 font-bold text-amber-600">P</span>
                            <CurrencyDisplay value={plannedAvg} />
                          </TableCell>
                          <TableCell className="text-center">{plannedCount}</TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={plannedTotal} />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">
                            <span className="mr-2 font-bold text-stone-700">R</span>
                            <CurrencyDisplay value={realizedAvg} />
                          </TableCell>
                          <TableCell className="text-center">{realizedCount}</TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={realizedTotal} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            );
          })()}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsFractionalAnalyticsDialogOpen(false);
                setFractionalAnalyticsRevenueId('');
              }}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar Recebimento Fracionado */}
      <Dialog
        open={isFractionalEntryEditDialogOpen}
        onOpenChange={(open) => {
          setIsFractionalEntryEditDialogOpen(open);
          if (!open) {
            setFractionalEntryToEdit(null);
            if (fractionalAnalyticsRevenueId) setIsFractionalAnalyticsDialogOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle>
              Editar{fractionalAnalyticsRevenue?.name ? ` - ${fractionalAnalyticsRevenue.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Ajuste os dados do recebimento fracionado selecionado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-8 py-2">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-amber-600">Planejado</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Data Prevista</Label>
                  <DateInput value={fractionalAnalyticsRevenue?.expected_date || ''} onChange={() => {}} disabled />
                </div>
                <div className="grid gap-2">
                  <Label>Valor Previsto</Label>
                  <CurrencyInput value={fractionalAnalyticsRevenue?.average_value || 0} onChange={() => {}} disabled />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Observação</Label>
                <Textarea value={fractionalAnalyticsRevenue?.notes || ''} onChange={() => {}} disabled />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-stone-700">Realizado</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>
                    Data Recebida <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <DateInput
                    value={fractionalEntryFormData.realization_date}
                    onChange={(value) =>
                      setFractionalEntryFormData((prev) => ({ ...prev, realization_date: value }))
                    }
                    placeholder="dd/mm/aaaa"
                    showSingleCalendar
                  />
                </div>
                <div className="grid gap-2">
                  <Label>
                    Valor Recebido <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <CurrencyInput
                    value={fractionalEntryFormData.amount || 0}
                    onChange={(value) =>
                      setFractionalEntryFormData((prev) => ({ ...prev, amount: value.toString() }))
                    }
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>
                    Quem recebeu ? <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={fractionalEntryFormData.agent_id}
                    onValueChange={(value) =>
                      setFractionalEntryFormData((prev) => ({ ...prev, agent_id: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                    {revenueAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}{agent.is_default_agent ? ' (Padrão)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>
                    Forma de Recebimento <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={fractionalEntryFormData.receipt_method}
                    onValueChange={(value) =>
                      setFractionalEntryFormData((prev) => ({ ...prev, receipt_method: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {receiptMethodsList?.items?.map((item) => (
                        <SelectItem key={item.id} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Observação</Label>
                <Textarea
                  value={fractionalEntryFormData.notes}
                  onChange={(e) =>
                    setFractionalEntryFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelFractionalEntryEdit}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveFractionalEntryEdit}
              disabled={
                isUpdatingFractionalRealization ||
                !fractionalEntryFormData.amount ||
                !fractionalEntryFormData.realization_date ||
                !fractionalEntryFormData.agent_id ||
                !fractionalEntryFormData.receipt_method
              }
              className="bg-stone-700 hover:bg-stone-800"
            >
              {isUpdatingFractionalRealization ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Realize Revenue Dialog */}
      <Dialog open={isRealizeDialogOpen} onOpenChange={setIsRealizeDialogOpen}>
        <DialogContent data-tour-id="revenue-realize-dialog" className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Receber{selectedRevenue?.name ? ` - ${selectedRevenue.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do recebimento. Campos com * são obrigatórios.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            <div className="grid gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-amber-500">Planejado</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="planned_expected_date">Data Prevista</Label>
                  <DateInput
                    id="planned_expected_date"
                    value={selectedRevenue?.expected_date || ''}
                    disabled
                    placeholder="—"
                    showSingleCalendar
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="planned_amount_display">Valor Previsto</Label>
                  <CurrencyInput
                    id="planned_amount_display"
                    value={Number(selectedRevenue ? getRevenuePlannedAmountForPO(selectedRevenue) : 0)}
                    onChange={() => undefined}
                    disabled
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="planned_notes_display">Observação</Label>
                <Textarea
                  id="planned_notes_display"
                  value={selectedRevenue?.notes || ''}
                  disabled
                  placeholder="—"
                />
              </div>
            </div>

            <div className="grid gap-3 pt-2">
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-stone-700 dark:text-stone-200">
                  Realizado
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realized_amount">
                  Valor recebido <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <CurrencyInput
                  id="realized_amount"
                  value={realizeFormData.amount}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, amount: value.toString() })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="receipt_method">
                  Forma de recebimento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select 
                  value={realizeFormData.receipt_method} 
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, receipt_method: value })}
                >
                  <SelectTrigger aria-label="Forma de recebimento">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {receiptMethodsList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realization_date">
                  Data do recebimento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <DateInput
                  id="realization_date"
                  value={realizeFormData.realization_date}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, realization_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="agent_id">
                  Quem recebeu <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select 
                  value={realizeFormData.agent_id} 
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, agent_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRealizeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleRealizeRevenue}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.revenueConfirm}
              disabled={
                !realizeFormData.amount ||
                !realizeFormData.realization_date ||
                !realizeFormData.agent_id ||
                !realizeFormData.receipt_method
              }
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Realized Revenue Dialog */}
      <Dialog open={isEditRealizedDialogOpen} onOpenChange={setIsEditRealizedDialogOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Editar Realizado{selectedRevenue?.name ? ` - ${selectedRevenue.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Edite os dados do recebimento. Campos com * são obrigatórios.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_amount">
                  Valor recebido <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <CurrencyInput
                  id="edit_realized_amount"
                  value={Number.parseFloat(editRealizedFormData.amount) || 0}
                  onChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, amount: value.toString() })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_receipt_method">
                  Forma de recebimento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select 
                  value={editRealizedFormData.receipt_method} 
                  onValueChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, receipt_method: value })}
                >
                  <SelectTrigger id="edit_realized_receipt_method" aria-label="Forma de recebimento">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {receiptMethodsList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_date">
                  Data do recebimento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <DateInput
                  id="edit_realized_date"
                  value={editRealizedFormData.realization_date}
                  onChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, realization_date: value })}
                  placeholder="Selecione a data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_agent_id">
                  Quem recebeu <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select 
                  value={editRealizedFormData.agent_id} 
                  onValueChange={(value) => setEditRealizedFormData({ ...editRealizedFormData, agent_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => setIsUndoRealizedDialogOpen(true)}
              disabled={isUndoingRealization}
              title="Excluir realizado"
              aria-label="Excluir realizado"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive sm:mr-auto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => setIsEditRealizedDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateRealized}
              disabled={
                !editRealizedFormData.amount ||
                !editRealizedFormData.realization_date ||
                !editRealizedFormData.agent_id ||
                !editRealizedFormData.receipt_method
              }
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo Realized Revenue Confirmation Dialog */}
      <Dialog open={isUndoRealizedDialogOpen} onOpenChange={setIsUndoRealizedDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              Excluir realizado{selectedRevenue?.name ? ` - ${selectedRevenue.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja excluir o realizado? Os dados do recebimento serão removidos
              e a receita voltará para planejado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsUndoRealizedDialogOpen(false)}
              disabled={isUndoingRealization}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmUndoRealized}
              disabled={isUndoingRealization}
            >
              {isUndoingRealization ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Revenue Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-tour-id="revenue-create-dialog" className="sm:max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Receita</DialogTitle>
            <DialogDescription>
              São os valores líquidos pagos a você - salário, comissão, vales etc. Veja a
              listagem.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">
                  Nome da receita <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="name"
                  mode="input"
                  value={formData.name}
                  onChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                  portalled={false}
                  options={(revenueNamesList?.items || []).map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))}
                  placeholder=""
                  emptyText="Nenhum nome encontrado"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="revenue_type">
                  Tipo <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.revenue_type}
                  onValueChange={revenueTypeGate.guardChange((value: RevenueType) => setFormData((prev) => ({ ...prev, revenue_type: value })))}
                >
                  <SelectTrigger id="revenue_type">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="continuous">{revenueTypeGate.itemContent('continuous', 'Contínua')}</SelectItem>
                    <SelectItem value="installment">{revenueTypeGate.itemContent('installment', 'Parcelada')}</SelectItem>
                    <SelectItem value="single">{revenueTypeGate.itemContent('single', 'Única')}</SelectItem>
                    <SelectItem value="fractional">{revenueTypeGate.itemContent('fractional', 'Fracionada')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Installment-specific fields */}
            {formData.revenue_type === 'installment' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="total_installments">
                    Total de parcelas <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="total_installments"
                    type="number"
                    min="1"
                    placeholder="Ex: 12"
                    value={formData.total_installments}
                    onChange={(e) => setFormData((prev) => ({ ...prev, total_installments: e.target.value }))}
                    className={createFormTotalInstallmentError ? 'border-destructive' : ''}
                    aria-invalid={!!createFormTotalInstallmentError}
                    aria-describedby={createFormTotalInstallmentError ? 'revenue-total-installments-error' : undefined}
                  />
                  {createFormTotalInstallmentError && (
                    <p id="revenue-total-installments-error" className="text-sm text-destructive">
                      {createFormTotalInstallmentError}
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="remaining_installments">
                    Parcelas restantes <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="remaining_installments"
                    type="number"
                    min="1"
                    placeholder="Ex: 10"
                    value={formData.remaining_installments}
                    onChange={(e) => setFormData((prev) => ({ ...prev, remaining_installments: e.target.value }))}
                    className={createFormRemainingInstallmentError ? 'border-destructive' : ''}
                    aria-invalid={!!createFormRemainingInstallmentError}
                    aria-describedby={
                      createFormRemainingInstallmentError ? 'revenue-remaining-installments-error' : undefined
                    }
                  />
                  {createFormRemainingInstallmentError && (
                    <p id="revenue-remaining-installments-error" className="text-sm text-destructive">
                      {createFormRemainingInstallmentError}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Fractional-specific fields */}
            {formData.revenue_type === 'fractional' && (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border bg-muted/30 p-3">
                  <p className="text-xs text-muted-foreground">
                    Copie receitas fracionadas de períodos anteriores para agilizar seu cadastro.
                  </p>
                  {/* Cópia de receitas - requer copia-de-receitas */}
                  <FeatureGuard
                    feature="copia-de-receitas"
                    fallback={<FeatureLockButton featureName="Cópia de Receitas" label="Copiar dados anteriores" size="sm" />}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCopyPreviousDialogOpen(true)}
                      className="gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Copiar dados anteriores
                    </Button>
                  </FeatureGuard>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="frequency">
                      Frequência <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="frequency"
                      type="number"
                      min="1"
                      placeholder="Ex: 2"
                      value={formData.frequency}
                      onChange={(e) => setFormData((prev) => ({ ...prev, frequency: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="average_value">
                      Valor Médio Planejado <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="average_value"
                      value={formData.average_value || 0}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, average_value: value.toString() }))
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="amount">
                    Valor total a receber <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <CurrencyInput
                    id="amount"
                    value={formData.planned_amount || 0}
                    onChange={(value) => setFormData((prev) => ({ ...prev, planned_amount: value.toString() }))}
                    disabled
                    className="bg-muted"
                  />
                  {formData.frequency && formData.average_value && (
                    <p className="text-xs text-muted-foreground">
                      {formData.frequency}x × R${' '}
                      {Number(formData.average_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} = R${' '}
                      {Number(formData.planned_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              </>
            )}

            {formData.revenue_type !== 'fractional' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="amount">
                    {formData.revenue_type === 'installment' ? 'Valor da parcela a receber' : 'Valor a receber'}{' '}
                    <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <CurrencyInput
                    id="amount"
                    value={formData.planned_amount || 0}
                    onChange={(value) => setFormData((prev) => ({ ...prev, planned_amount: value.toString() }))}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="expected_date">
                    Data{' '}
                    {(formData.revenue_type === 'continuous' ||
                      formData.revenue_type === 'single' ||
                      formData.revenue_type === 'installment') && (
                      <span aria-hidden="true" className="text-destructive">*</span>
                    )}
                  </Label>
                  <DateInput
                    id="expected_date"
                    value={formData.expected_date}
                    onChange={(value) => setFormData((prev) => ({ ...prev, expected_date: value }))}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="receipt_method">
                  Fonte Pagadora{' '}
                  {(formData.revenue_type === 'continuous' ||
                    formData.revenue_type === 'single' ||
                    formData.revenue_type === 'installment' ||
                    formData.revenue_type === 'fractional') && (
                    <span aria-hidden="true" className="text-destructive">*</span>
                  )}
                </Label>
                <Input
                  id="receipt_method"
                  value={formData.receipt_method}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, receipt_method: event.target.value }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="revenue-agent">
                  Agente da Receita{' '}
                  {(formData.revenue_type === 'continuous' ||
                    formData.revenue_type === 'single' ||
                    formData.revenue_type === 'installment' ||
                    formData.revenue_type === 'fractional') && (
                    <span aria-hidden="true" className="text-destructive">*</span>
                  )}
                </Label>
                <Select
                  value={formData.agent_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, agent_id: value }))}
                >
                  <SelectTrigger id="revenue-agent" disabled={!agents?.length}>
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder=""
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateRevenue}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.revenueSave}
              // Formulário completo fica verde: sinaliza que o cadastro pode ser criado.
              className={isCreateFormComplete && !isCreating ? CREATE_READY_BUTTON_CLASS : undefined}
              disabled={isCreating || !isCreateFormComplete}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar Receita'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Fractional Revenues (Planejamento/Realização) */}
      <Dialog open={isCopyPreviousDialogOpen} onOpenChange={setIsCopyPreviousDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Copiar dados anteriores</DialogTitle>
            <DialogDescription>
              Escolha o período e selecione quais receitas fracionadas deseja copiar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Copiar dados de</Label>
                <Select value={copyDataScope} onValueChange={(value: 'planned' | 'realized') => setCopyDataScope(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planejamento</SelectItem>
                    <SelectItem value="realized">Realizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Período Orçamentário</Label>
                <Select value={copySourcePeriodId} onValueChange={setCopySourcePeriodId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um período" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      if (isCopySourcesLoading) return (
                          <SelectItem value="__loading__" disabled>
                            Carregando períodos...
                          </SelectItem>
                      );
                      if (filteredCopySourcePeriods.length === 0) return (
                          <SelectItem value="__empty__" disabled>
                            Nenhum período anterior com dados
                          </SelectItem>
                      );
                      return (
                          filteredCopySourcePeriods.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              PO {String(getPONumberByStartDate(p.start_date, p.period_number)).padStart(2, '0')} •{' '}
                              {formatPeriodLabel(p.start_date, p.end_date)}
                            </SelectItem>
                          ))
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]">
                      <Checkbox
                        checked={allCopyRevenuesSelected ? true : someCopyRevenuesSelected ? 'indeterminate' : false}
                        onCheckedChange={(checked) => handleToggleCopyRevenueAll(checked === true)}
                        aria-label="Selecionar todas"
                      />
                    </TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-center">Ocorrências</TableHead>
                    <TableHead className="text-right">Valor Médio</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Agente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isCopySourceRevenuesLoading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        Carregando receitas...
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceRevenuesLoading && !copySourcePeriodId && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        Selecione um período para listar as receitas.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceRevenuesLoading && copySourcePeriodId && copyCandidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        Nenhuma receita disponível para copiar.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceRevenuesLoading &&
                    copySourcePeriodId &&
                    copyCandidates.map((revenue) => {
                      const selected = selectedCopyRevenueIds.includes(revenue.id);
                      const { realizedCount, realizedTotal, averageRealized } = getFractionalCopyStats(revenue);
                      const displayFrequency =
                        copyDataScope === 'realized' ? realizedCount : Number(revenue.frequency || 0);
                      const displayAverage =
                        copyDataScope === 'realized' ? averageRealized : Number(revenue.average_value || 0);
                      const displayTotal =
                        copyDataScope === 'realized' ? realizedTotal : Number(revenue.planned_amount || 0);

                      return (
                        <TableRow key={revenue.id} data-state={selected ? 'selected' : undefined}>
                          <TableCell className="w-[48px]">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(checked) =>
                                handleToggleCopyRevenueSelection(revenue.id, checked === true)
                              }
                              aria-label={`Selecionar ${revenue.name}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{revenue.name}</TableCell>
                          <TableCell className="text-center">{displayFrequency}</TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={displayAverage} />
                          </TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={displayTotal} />
                          </TableCell>
                          <TableCell>{revenue.agent?.name || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{selectedCopyRevenueCount} de {copyCandidates.length} selecionadas</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setIsCopyPreviousDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleCopySelectedRevenues}
              disabled={isCopying || !copySourcePeriodId || !copyTargetPeriodId || selectedCopyRevenueCount === 0}
            >
              {isCopying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Copiando...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Revenue Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Receita</DialogTitle>
            <DialogDescription>
              Altere os dados da receita "{revenueToEdit?.name}".
            </DialogDescription>
          </DialogHeader>
          {revenueToEdit?.is_balance_carryover && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 mt-1">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs leading-snug text-amber-800 dark:text-amber-300">
                Atenção: alterar este valor influencia o saldo do próximo período orçamentário.
                Por ser o Saldo do PO anterior, qualquer nova alteração no período anterior
                substituirá sua edição.
              </p>
            </div>
          )}
          <div className="grid gap-4 py-4 px-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_name">Nome da receita</Label>
                <Combobox
                  id="edit_name"
                  mode="input"
                  value={editFormData.name}
                  onChange={(value) => setEditFormData({ ...editFormData, name: value })}
                  portalled={false}
                  options={(revenueNamesList?.items || []).map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))}
                  placeholder="Selecione ou digite..."
                  emptyText="Nenhum nome encontrado"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_type">
                  Tipo de receita <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select
                  value={editFormData.revenue_type}
                  onValueChange={revenueTypeGate.guardChange((value: RevenueType) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      revenue_type: value,
                    }))
                  )}
                >
                  <SelectTrigger id="edit_type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="continuous">{revenueTypeGate.itemContent('continuous', '🔄 Contínua (todo mês)')}</SelectItem>
                    <SelectItem value="single">{revenueTypeGate.itemContent('single', '1️⃣ Única (recebimento único)')}</SelectItem>
                    <SelectItem value="installment">{revenueTypeGate.itemContent('installment', '📊 Parcelada')}</SelectItem>
                    <SelectItem value="fractional">{revenueTypeGate.itemContent('fractional', '📈 Fracionada')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editFormData.revenue_type === 'installment' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_total_installments">
                    Total de parcelas <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="edit_total_installments"
                    type="number"
                    min="1"
                    placeholder="Ex: 12"
                    value={editFormData.total_installments}
                    onChange={(e) =>
                      setEditFormData((prev) => ({ ...prev, total_installments: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_remaining_installments">
                    Parcelas restantes <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="edit_remaining_installments"
                    type="number"
                    min="1"
                    placeholder="Ex: 10"
                    value={editFormData.remaining_installments}
                    onChange={(e) =>
                      setEditFormData((prev) => ({ ...prev, remaining_installments: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}

            {editFormData.revenue_type === 'fractional' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_frequency">
                      Frequência <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit_frequency"
                      type="number"
                      min="1"
                      placeholder="Ex: 2"
                      value={editFormData.frequency}
                      onChange={(e) =>
                        setEditFormData((prev) => ({ ...prev, frequency: e.target.value }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_average_value">
                      Valor Médio Planejado <span aria-hidden="true" className="text-destructive">*</span>
                    </Label>
                    <CurrencyInput
                      id="edit_average_value"
                      value={editFormData.average_value || 0}
                      onChange={(value) =>
                        setEditFormData((prev) => ({ ...prev, average_value: value.toString() }))
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit_amount">
                    Valor total a receber <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <CurrencyInput
                    id="edit_amount"
                    value={editFormData.planned_amount || 0}
                    onChange={(value) =>
                      setEditFormData((prev) => ({ ...prev, planned_amount: value.toString() }))
                    }
                    disabled
                    className="bg-muted"
                  />
                  {editFormData.frequency && editFormData.average_value && (
                    <p className="text-xs text-muted-foreground">
                      {editFormData.frequency}x × R${' '}
                      {Number(editFormData.average_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} = R${' '}
                      {Number(editFormData.planned_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              </>
            )}

            {editFormData.revenue_type !== 'fractional' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_amount">
                    {editFormData.revenue_type === 'installment'
                      ? 'Valor da parcela a receber'
                      : 'Valor a receber'}{' '}
                    <span aria-hidden="true" className="text-destructive">*</span>
                  </Label>
                  <CurrencyInput
                    id="edit_amount"
                    value={editFormData.planned_amount || 0}
                    onChange={(value) =>
                      setEditFormData((prev) => ({ ...prev, planned_amount: value.toString() }))
                    }
                  />
                  {editFormData.revenue_type === 'installment' &&
                    revenueToEdit?.revenue_type === 'installment' &&
                    !!revenueToEdit.total_installments && (
                      <p className="text-xs text-muted-foreground">
                        Parcela neste PO{' '}
                        {revenueToEdit.current_installment
                          ? `(${revenueToEdit.current_installment}/${revenueToEdit.total_installments})`
                          : ''}{' '}
                        R${' '}
                        {getRevenuePlannedAmountForPO(revenueToEdit).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                    )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_expected_date">
                    Data{' '}
                    {(editFormData.revenue_type === 'continuous' ||
                      editFormData.revenue_type === 'single' ||
                      editFormData.revenue_type === 'installment') && (
                      <span aria-hidden="true" className="text-destructive">*</span>
                    )}
                  </Label>
                  <DateInput
                    id="edit_expected_date"
                    value={editFormData.expected_date}
                    onChange={(value) => setEditFormData({ ...editFormData, expected_date: value })}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
              </div>
            )}

            {/* Campo para editar valor realizado - exibido quando já há valor realizado */}
            {(revenueToEdit?.status === 'realized' || revenueToEdit?.status === 'partially_realized') && (
              <div className="grid gap-2">
                <Label htmlFor="edit_realized_amount">Valor realizado (R$)</Label>
                <CurrencyInput
                  id="edit_realized_amount"
                  value={editFormData.realized_amount || 0}
                  onChange={(value) => setEditFormData({ ...editFormData, realized_amount: value.toString() })}
                />
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_receipt_method">Fonte Pagadora</Label>
                <Input
                  id="edit_receipt_method"
                  value={editFormData.receipt_method}
                  onChange={(event) =>
                    setEditFormData((prev) => ({ ...prev, receipt_method: event.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_agent_id">Agente da Receita</Label>
                <Select
                  value={editFormData.agent_id}
                  onValueChange={(value) =>
                    setEditFormData((prev) => ({ ...prev, agent_id: value }))
                  }
                >
                  <SelectTrigger id="edit_agent_id">
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                        {agent.is_default_agent ? ' (Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_notes">Observações (opcional)</Label>
              <Textarea
                id="edit_notes"
                placeholder="Notas adicionais..."
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAttemptUpdateRevenue}
              disabled={isUpdating || !editFormData.name || !editFormData.planned_amount}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scope Dialog (Contínua / Parcelada) */}
      <Dialog open={isEditScopeDialogOpen} onOpenChange={setIsEditScopeDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Editar {revenueToEdit?.name || 'Receita'}</DialogTitle>
            <DialogDescription>De que forma deseja editar esta receita?</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <RadioGroup value={editScope} onValueChange={(v) => setEditScope(v as 'current' | 'future')}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="current" id="edit-scope-current" />
                <Label htmlFor="edit-scope-current" className="font-normal cursor-pointer">
                  Somente para este PO
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="future" id="edit-scope-future" />
                <div className="space-y-1">
                  <Label htmlFor="edit-scope-future" className="font-normal cursor-pointer">
                    Para este PO e os POs seguintes
                  </Label>
                  <p className="text-xs text-muted-foreground">*Esta ação não poderá ser desfeita</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsEditScopeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmEditScope} disabled={isUpdating}>
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type-Change Dialog (informa que a mudança de tipo afeta este PO e os seguintes) */}
      <Dialog open={isTypeChangeDialogOpen} onOpenChange={setIsTypeChangeDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Alterar tipo de {revenueToEdit?.name || 'Receita'}</DialogTitle>
            <DialogDescription>
              Ao alterar o tipo da receita, a mudança será aplicada no PO atual e nos POs seguintes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-muted-foreground">*Esta ação não poderá ser desfeita</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsTypeChangeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmTypeChange} disabled={isUpdating}>
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir receitas selecionadas</DialogTitle>
            <DialogDescription>
              Esta ação excluirá todas as receitas contínuas e parceladas dos POs futuros. Deseja confirmar a exclusão?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)} disabled={isBulkDeleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteRevenues}
              disabled={isBulkDeleting || selectedRevenueCount === 0}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog (Única / Fracionada) */}
      <Dialog open={isDeleteConfirmDialogOpen} onOpenChange={setIsDeleteConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir {revenueToDelete?.name || 'Receita'}</DialogTitle>
            <DialogDescription>Tem certeza de que deseja excluir?</DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDeleteConfirmDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleConfirmDelete(false)}
              disabled={isDeletingRevenue}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Scope Dialog (Contínua / Parcelada) */}
      <Dialog open={isDeleteScopeDialogOpen} onOpenChange={setIsDeleteScopeDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir {revenueToDelete?.name || 'Receita'}</DialogTitle>
            <DialogDescription>De que forma deseja excluir?</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <RadioGroup
              value={deleteScope}
              onValueChange={(v) => setDeleteScope(v as 'current' | 'future')}
            >
              <div className="flex items-start gap-3">
                <RadioGroupItem value="current" id="delete-scope-current" />
                <Label htmlFor="delete-scope-current" className="font-normal cursor-pointer">
                  Somente para este PO
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="future" id="delete-scope-future" />
                <div className="space-y-1">
                  <Label htmlFor="delete-scope-future" className="font-normal cursor-pointer">
                    Para este PO e os POs seguintes
                  </Label>
                  <p className="text-xs text-muted-foreground">*Esta ação não poderá ser desfeita</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDeleteScopeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleConfirmDelete(deleteScope === 'future')}
              disabled={isDeletingRevenue}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fractional Realization Dialog */}
      <Dialog open={isFractionalDialogOpen} onOpenChange={setIsFractionalDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Registrar Recebimento Fracionado</DialogTitle>
            <DialogDescription>
              Adicione um recebimento parcial para "{selectedRevenue?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Summary Card */}
            <Card className="bg-muted/50">
              <CardContent className="py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Meta total:</span>
                  <CurrencyDisplay value={selectedRevenue?.planned_amount || 0} className="font-medium" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Já recebido:</span>
                  <CurrencyDisplay value={selectedRevenue?.realized_amount || 0} className="font-medium" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Recebimentos:</span>
                  <span className="font-medium">
                    {selectedRevenue?.fractional_realizations?.length || 0} de {selectedRevenue?.frequency || 0}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-2 border-t">
                  <span>Faltam:</span>
                  <CurrencyDisplay 
                    value={(selectedRevenue?.planned_amount || 0) - (selectedRevenue?.realized_amount || 0)} 
                    colorize 
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor médio sugerido:</span>
                  <CurrencyDisplay value={selectedRevenue?.average_value || 0} className="text-primary font-medium" />
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="fractional_amount">Valor recebido (R$)</Label>
                {selectedRevenue?.fractional_realizations && selectedRevenue.fractional_realizations.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2 text-primary"
                    onClick={() => {
                      // Copia dados da última realização
                      const lastRealization = selectedRevenue.fractional_realizations![selectedRevenue.fractional_realizations!.length - 1];
                      setFractionalFormData(prev => ({
                        ...prev,
                        amount: lastRealization.amount?.toString() || prev.amount,
                        notes: lastRealization.notes || prev.notes,
                      }));
                    }}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copiar última realização
                  </Button>
                )}
              </div>
              <CurrencyInput
                id="fractional_amount"
                value={fractionalFormData.amount || 0}
                onChange={(value) => setFractionalFormData({ ...fractionalFormData, amount: value.toString() })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fractional_date">Data do recebimento</Label>
              <DateInput
                id="fractional_date"
                value={fractionalFormData.realization_date}
                onChange={(value) => setFractionalFormData({ ...fractionalFormData, realization_date: value })}
                placeholder="Selecione a data"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="fractional_agent">
                  Quem recebeu? <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select
                  value={fractionalFormData.agent_id}
                  onValueChange={(value) => setFractionalFormData({ ...fractionalFormData, agent_id: value })}
                >
                  <SelectTrigger id="fractional_agent">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}{agent.is_default_agent ? ' (Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="fractional_receipt_method">
                  Forma de recebimento <span aria-hidden="true" className="text-destructive">*</span>
                </Label>
                <Select
                  value={fractionalFormData.receipt_method}
                  onValueChange={(value) => setFractionalFormData({ ...fractionalFormData, receipt_method: value })}
                >
                  <SelectTrigger id="fractional_receipt_method" aria-label="Forma de recebimento">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {receiptMethodsList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fractional_notes">Observações (opcional)</Label>
              <Input
                id="fractional_notes"
                placeholder="Ex: Trabalho entregue dia 15..."
                value={fractionalFormData.notes}
                onChange={(e) => setFractionalFormData({ ...fractionalFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFractionalDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddFractionalRealization}
              disabled={
                isAddingFractional ||
                !fractionalFormData.amount ||
                !fractionalFormData.realization_date ||
                !fractionalFormData.agent_id ||
                !fractionalFormData.receipt_method
              }
            >
              {isAddingFractional ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                'Registrar Recebimento'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RevenuesPage;
